import { type NestedTransitionProps, Transition, type TransitionStatus } from "@mdotm/shadcn/legacy/components";
import { overrideClassList, toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { type RwAtom, useAtomState, WithRwAtom } from "@mdotm/shadcn/legacy/reactivity";
import { alwaysArray } from "@mdotm/shadcn/legacy/utils";
import { createContext, type ReactNode, useCallback, useMemo } from "react";

type StackContextBlockProps = Pick<StackRouterProps, "pop" | "push" | "reset" | "stack$"> & {
	stackIndex: number;
};

export const StackContextBlock = createContext<StackContextBlockProps | null>(null);

function StackContextBlockProvider({ children, ctx }: { children: ReactNode; ctx: StackContextBlockProps }) {
	return <StackContextBlock.Provider value={ctx}>{children}</StackContextBlock.Provider>;
}

type Stack = {
	items: Array<React.ComponentType>;
	currentIndex: number;
	targetIndex: number;
	skipTransition?: boolean;
};

export type StackRouterProps = {
	pop(): void;
	push(nodes: React.ComponentType | Array<React.ComponentType>, opts?: { skipTransition?: boolean }): void;
	reset(): void;
	stack$: RwAtom<Stack>;
};

export function useStackRouter(params: { root: React.ComponentType }): StackRouterProps {
	const [stack$, setStack] = useAtomState<Stack>({
		items: [params.root],
		currentIndex: 0,
		targetIndex: 0,
	});

	const push = useCallback<StackRouterProps["push"]>(
		(node, opts) => {
			setStack((_stack) => {
				const newItems = alwaysArray(node);
				const items = [..._stack.items, ...newItems];
				return {
					items,
					currentIndex: _stack.currentIndex,
					targetIndex: items.length - 1,
					skipTransition: opts?.skipTransition,
				};
			});
		},
		[setStack],
	);

	const pop = useCallback<StackRouterProps["pop"]>(() => {
		setStack((_stack) => ({
			..._stack,
			currentIndex: _stack.currentIndex,
			targetIndex: _stack.currentIndex - 1,
		}));
	}, [setStack]);

	const reset = useCallback(() => {
		setStack({
			items: [params.root],
			currentIndex: 0,
			targetIndex: 0,
		});
	}, [setStack, params.root]);

	return useMemo(() => ({ pop, push, reset, stack$ }), [pop, push, reset, stack$]);
}

type AnimationType = "slide" | "fade";

type StackTransitionProps = {
	type: AnimationType;
	stack: Stack;
	stackIndex: number;
	children: ReactNode;
	onTransitionStatusChange?: (state: TransitionStatus) => void;
};

function StackTransition({ type, stack, stackIndex, children, onTransitionStatusChange }: StackTransitionProps) {
	const isTargetItem = stack.targetIndex === stackIndex;
	const isPushingForward = stack.currentIndex < stack.targetIndex;
	const isPoppingBack = stack.currentIndex > stack.targetIndex;

	const isEnteringFromBelow = (isTargetItem && isPushingForward) || (!isTargetItem && isPoppingBack);
	const isEnteringFromAbove = !isEnteringFromBelow;

	const animations = {
		slide: {
			enterFromClassName: {
				"opacity-0": true,
				"translate-x-full": isEnteringFromBelow,
				"-translate-x-full": isEnteringFromAbove,
			},
			enterToClassName: {
				"translate-x-0 shadow-xl opacity-100": true,
			},
		},
		fade: {
			enterFromClassName: {
				"opacity-0": true,
			},
			enterToClassName: {
				"opacity-100": true,
			},
		},
	} satisfies Record<AnimationType, Pick<NestedTransitionProps, "enterFromClassName" | "enterToClassName">>;

	const selectedAnimation = animations[type];

	return (
		<Transition
			initialIn={stackIndex === 0}
			onChange={onTransitionStatusChange}
			in={isTargetItem}
			unmountOnExit
			duration={stack.skipTransition ? 0 : 300}
			className={overrideClassList(
				"max-w-full h-full transition-all transition-discrete ease-in-out duration-300 flex flex-col bg-white",
			)}
			enterFromClassName={selectedAnimation.enterFromClassName}
			enterToClassName={selectedAnimation.enterToClassName}
			exitedClassName="invisible"
		>
			{({ className: animatedClassList, style: animatedStyle, transitionStatus }) => (
				<div data-transitionstatus={transitionStatus} className={toClassName(animatedClassList)} style={animatedStyle}>
					{children}
				</div>
			)}
		</Transition>
	);
}

type StackOutletProps = StackRouterProps & {
	animation?: AnimationType;
};

export function StackOutlet({ animation = "slide", ...routerProps }: StackOutletProps) {
	const { stack$ } = routerProps;
	return (
		<WithRwAtom atom$={stack$}>
			{([stack, setStack]) => {
				const transitionHandler = (stackIndex: number) => (state: TransitionStatus) => {
					if (stackIndex !== stack.items.length - 1) {
						// only the last item in the stack should update the state
						return;
					}
					const isTargetItem = stack.targetIndex === stackIndex;
					const isExitingBackward = stack.targetIndex < stackIndex;

					// When a screen exits while popping back, remove it from the stack
					if (state === "exited" && isExitingBackward) {
						setStack((prevStack) => {
							const items = prevStack.items.slice(0, -1);
							return {
								items,
								currentIndex: items.length - 1,
								targetIndex: items.length - 1,
								skipTransition: undefined,
							};
						});
						return;
					}

					// When the target screen enters, update currentIndex
					if (state === "entered" && isTargetItem) {
						setStack((prevStack) => ({
							...prevStack,
							currentIndex: prevStack.items.length - 1,
							targetIndex: prevStack.items.length - 1,
							skipTransition: undefined,
						}));
						return;
					}
				};

				return (
					<div className="grid grid-cols-1 h-full bg-white">
						<ForEach collection={stack.items}>
							{(Component, stackIndex) => (
								<div className="col-start-1 row-start-1 h-full relative overflow-hidden">
									<StackContextBlockProvider ctx={{ stackIndex, ...routerProps }}>
										<StackTransition
											type={animation}
											stack={stack}
											stackIndex={stackIndex}
											onTransitionStatusChange={transitionHandler(stackIndex)}
										>
											<Component />
										</StackTransition>
									</StackContextBlockProvider>
								</div>
							)}
						</ForEach>
					</div>
				);
			}}
		</WithRwAtom>
	);
}
